import { ref } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'

export default function useContentPackAcademies() {
  const RESOURCE = '/content-pack-academy'
  const { showSnackbar } = useSnackbar()
  const isLoading = ref(false)

  const fetchContentPacksByAcademyId = async academyId => {
    isLoading.value = true
    try {
      const response = await axios.get(`${RESOURCE}/academy/${academyId}/content-packs`, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      return response.data || []
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to fetch content packs for academy:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchAcademiesByContentPackId = async contentPackId => {
    isLoading.value = true
    try {
      const response = await axios.get(`${RESOURCE}/content-pack/${contentPackId}/academies`, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      return response.data || []
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to fetch academies for content pack:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const upsertContentPackAcademy = async (academyId, contentPackId, options = {}) => {
    isLoading.value = true
    try {
      await axios.post(RESOURCE, {
        academyId,
        contentPackId,
        purchased: options.purchased,
      }, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      if (!options.silent) {
        showSnackbar('Academy content packs updated successfully', SnackbarType.SUCCESS)
      }
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to update academy content packs:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const formatPurchased = purchased => (purchased ? 'Yes' : 'No')

  const deleteContentPackAcademy = async (academyId, contentPackId, options = {}) => {
    isLoading.value = true
    try {
      await axios.delete(RESOURCE, {
        data: {
          academyId,
          contentPackId,
        },
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      if (!options.silent) {
        showSnackbar('Academy content packs updated successfully', SnackbarType.SUCCESS)
      }
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to update academy content packs:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    fetchContentPacksByAcademyId,
    fetchAcademiesByContentPackId,
    upsertContentPackAcademy,
    deleteContentPackAcademy,
    formatPurchased,
  }
}
